export type TabKey = "today" | "mylist" | "team" | "admin" | "more";

const BASE_TABS: { key: TabKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "mylist", label: "My list" },
];

// Sprint-003 A6: Team is visible ONLY to `can_hand_out_work` (admin/
// manager) — a member never sees this tab, matching the ADR-002 §5 authz
// matrix and design-spec-today-card.md §3.7 exactly.
const TEAM_TAB: { key: TabKey; label: string } = { key: "team", label: "Team" };

// Sprint-004 B4: Admin is visible ONLY to the admin role — never a manager
// user, even though both share `can_hand_out_work: true` (see
// `hooks/useIsAdmin.ts` for how the client distinguishes the two).
const ADMIN_TAB: { key: TabKey; label: string } = { key: "admin", label: "Admin" };

// Sprint-005 C4: More is visible to EVERY authed role, always last —
// member/manager/admin alike (its own screen further role-gates which of
// its rows appear; see `views/MoreView.tsx`). A deliberate "More" landing
// pattern rather than adding Animals/Projects as two more top-level tabs,
// which would crowd the bar to 5-6 items — flagged for design QA: an
// admin session now sees 5 tabs total (Today, My list, Team, Admin, More),
// up from today's 4-item max.
const MORE_TAB: { key: TabKey; label: string } = { key: "more", label: "More" };

export function TabBar({
  active,
  onChange,
  showTeam,
  showAdmin = false,
  mylistUnread = false,
}: {
  active: TabKey;
  onChange: (tab: TabKey) => void;
  showTeam: boolean;
  showAdmin?: boolean;
  /** Sprint-017 E7 (design §7.4) — the project-agnostic newness marker on
   * the My list tab (a de-identified assigned task arrived). Only ever on
   * My list; never on a Projects entry (that would leak project origin).
   * UX review (done-feedback): rendered as a distinct `.tab-badge`, no
   * longer as the active-tab `.tab-dot` (CD MUST-FIX #2's "reuse the dot"
   * made unread indistinguishable from active). */
  mylistUnread?: boolean;
}) {
  const tabs = [
    ...BASE_TABS,
    ...(showTeam ? [TEAM_TAB] : []),
    ...(showAdmin ? [ADMIN_TAB] : []),
    MORE_TAB,
  ];
  return (
    <nav class="tab-bar" aria-label="Main">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          class={`tab-btn${active === tab.key ? " is-active" : ""}`}
          // Sprint-016 F3 (SYS-03, ux-audit-whole-app-2026-07-11.md — S3)
          // — `aria-current="page"` matches `Sidebar.tsx`'s own
          // `.sidebar-nav-item` exactly: both are the same "which
          // top-level view is this" concept, and `aria-current` is the
          // semantically correct pattern (WCAG 4.1.2) — `aria-pressed`
          // specifically implies two-state TOGGLE semantics, which this
          // never was. `.tab-btn.is-active`'s visual styling is driven by
          // the `is-active` class, untouched by this attribute swap.
          aria-current={active === tab.key ? "page" : undefined}
          onClick={() => onChange(tab.key)}
        >
          {/* The dot above the label is the ACTIVE-tab indicator only.
           * UX review (done-feedback): unread used to reuse this same
           * dot (same pine, same spot), so "new items on My list" and
           * "you are on My list" looked identical. Unread is now its own
           * amber badge at the label's top-right, plus a text equivalent. */}
          <span class="tab-dot" aria-hidden="true" />
          <span class="tab-label">
            {tab.label}
            {tab.key === "mylist" && mylistUnread ? (
              <>
                <span class="tab-badge" aria-hidden="true" />
                <span class="sr-only">, new items</span>
              </>
            ) : null}
          </span>
        </button>
      ))}
    </nav>
  );
}
